// src/prototype/components/execute/ExecVendorEvaluation.tsx
// ============================================================
// Vendor Evaluation (Oct 21, v3): DVA's initial vendor evaluation for the use case, shown above
// the full landscape. Recommendation and Epic baseline, then per sourcing lot: the shortlist to
// bring in, near misses, and the full evaluation matrix. Weights are adjustable and re-rank live
// from stored criterion scores. Evidence only: never changes a VRI score.
// ============================================================
import React, { useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import {
  Award, CheckCircle2, AlertTriangle, SlidersHorizontal, RotateCcw, Download, ExternalLink, ListChecks, XCircle,
} from 'lucide-react';
import { SectionCard } from '../SectionCard';
import {
  CRITERIA, DEFAULT_WEIGHTS, RUBRIC_VERSION, rerankLot, isDefaultWeights,
  type VendorEvaluation, type Weights, type CriterionId,
} from '../../data/execute/vendorEvaluation';

const PATH_LABEL = { buy: 'Buy', build: 'Build on what you own', combine: 'Combine what you own with targeted purchases' } as const;

function scoreTone(s: number) {
  return s >= 4 ? 'bg-emerald-100 text-emerald-800' : s >= 3 ? 'bg-[#EEF2F7] text-[#18365E]' : s >= 2 ? 'bg-amber-50 text-amber-800' : 'bg-red-50 text-red-700';
}

function host(url: string) {
  try { return new URL(url).hostname.replace(/^www\./, ''); } catch { return url; }
}

function readWeights(key: string): Weights {
  try {
    const raw = localStorage.getItem(key);
    if (raw) return { ...DEFAULT_WEIGHTS, ...JSON.parse(raw) };
  } catch { /* storage unavailable */ }
  return { ...DEFAULT_WEIGHTS };
}

export const ExecVendorEvaluation: React.FC<{ evaluation: VendorEvaluation; clientName: string; useCaseTitle: string }> = ({ evaluation, clientName, useCaseTitle }) => {
  const storageKey = `dva_vendor_eval_weights_${evaluation.useCaseId}`;
  const [weights, setWeightsState] = useState<Weights>(() => readWeights(storageKey));
  const [showWeights, setShowWeights] = useState(false);
  const setWeights = (w: Weights) => {
    setWeightsState(w);
    try { localStorage.setItem(storageKey, JSON.stringify(w)); } catch { /* ignore */ }
  };
  const custom = !isDefaultWeights(weights);
  const lots = useMemo(() => evaluation.lots.map((lot) => ({ lot, ranked: rerankLot(lot, weights) })), [evaluation, weights]);
  const shortlistCount = lots.reduce((n, l) => n + l.ranked.filter((v) => v.liveStatus === 'shortlist').length, 0);
  const evaluatedCount = lots.reduce((n, l) => n + l.ranked.length, 0);
  const asOf = new Date(`${evaluation.asOf}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const exportShortlist = () => {
    const wb = XLSX.utils.book_new();
    const short: (string | number)[][] = [['Lot', 'Rank', 'Vendor', 'Score (0-100)', 'Why shortlisted', 'Key risk', 'Verify in demo', 'RFP questions']];
    const matrix: (string | number)[][] = [['Lot', 'Rank', 'Vendor', 'Status', 'Score (0-100)', ...CRITERIA.map((c) => `${c.name} (w${weights[c.id]})`), 'Needs verification']];
    const rationale: (string | number)[][] = [['Lot', 'Vendor', 'Criterion', 'Score', 'Rationale', 'Evidence']];
    for (const { lot, ranked } of lots) {
      for (const v of ranked) {
        if (v.liveStatus === 'shortlist') short.push([lot.name, v.liveRank, v.vendor, v.liveScore, v.whyShortlisted ?? '', v.keyRisk ?? '', (v.verifyInDemo ?? []).join('\n'), (v.rfpQuestions ?? []).join('\n')]);
        matrix.push([lot.name, v.liveRank, v.vendor, v.liveStatus, v.liveScore, ...CRITERIA.map((c) => v.scores[c.id]?.score ?? ''), CRITERIA.filter((c) => v.scores[c.id]?.verify).map((c) => c.short).join(', ')]);
        for (const c of CRITERIA) {
          const s = v.scores[c.id];
          if (s) rationale.push([lot.name, v.vendor, c.name, s.score, s.rationale, s.evidence.join(' | ')]);
        }
      }
    }
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([[`${clientName} · ${useCaseTitle} · vendor evaluation`], [`Rubric ${RUBRIC_VERSION}${custom ? ' (client-adjusted weights)' : ''} · as of ${asOf}`], [], ...short]), 'Shortlist');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(matrix), 'Evaluation matrix');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(rationale), 'Scores and evidence');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([['Vendor', 'Reason'], ...evaluation.screenedOut.map((s) => [s.vendor, s.reason])]), 'Screened out');
    XLSX.writeFile(wb, `${useCaseTitle.replace(/[^A-Za-z0-9]+/g, '_')}_Vendor_Shortlist.xlsx`);
  };

  return (
    <div className="space-y-4">
      {/* Recommendation */}
      <div className="rounded-xl border border-[#18365E]/25 bg-white p-4">
        <div className="flex items-start gap-3 flex-wrap">
          <Award className="w-5 h-5 text-[#FF6E4C] mt-0.5 shrink-0" />
          <div className="flex-1 min-w-[260px]">
            <p className="text-[10px] font-bold text-[#FF6E4C] uppercase tracking-wide">Initial vendor evaluation · {shortlistCount} to bring in from {evaluatedCount} evaluated</p>
            <p className="text-sm font-bold text-[#18365E] mt-0.5">{PATH_LABEL[evaluation.recommendation.deliveryPath]}</p>
            <p className="text-xs text-[#4b5563] mt-1 leading-relaxed max-w-4xl">{evaluation.recommendation.summary}</p>
            <p className="text-[10px] text-[#717182] mt-2">
              Rubric {RUBRIC_VERSION}{custom ? ' with your adjusted weights' : ''} · as of {asOf} · evaluated against {clientName}'s Epic stack and capacity · evidence only, does not change any score
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={() => setShowWeights((s) => !s)} className={`text-xs font-semibold border rounded-lg px-3 py-1.5 flex items-center gap-1.5 ${custom ? 'border-[#FF6E4C] text-[#FF6E4C]' : 'border-[#18365E]/30 text-[#18365E] hover:bg-[#F5F7FA]'}`}>
              <SlidersHorizontal className="w-3.5 h-3.5" /> {custom ? 'Weights adjusted' : 'Adjust weights'}
            </button>
            <button onClick={exportShortlist} className="text-xs font-semibold text-white bg-[#18365E] rounded-lg px-3 py-1.5 hover:bg-[#122a4a] flex items-center gap-1.5">
              <Download className="w-3.5 h-3.5" /> Shortlist (XLSX)
            </button>
          </div>
        </div>

        {showWeights && (
          <div className="mt-4 border-t border-[#f1f5f9] pt-3">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3">
              {CRITERIA.map((c) => (
                <label key={c.id} className="text-[11px] text-[#4b5563]">
                  <span className="flex justify-between"><span className="font-semibold text-[#18365E]">{c.name}</span><span className="tabular-nums">{weights[c.id]}</span></span>
                  <input type="range" min={0} max={40} step={1} value={weights[c.id]} aria-label={`${c.name} weight`}
                    onChange={(e) => setWeights({ ...weights, [c.id]: Number(e.target.value) })} className="w-full accent-[#FF6E4C]" />
                </label>
              ))}
            </div>
            <div className="flex items-center justify-between mt-2">
              <p className="text-[10px] text-[#717182]">Weights are relative; the shortlist re-ranks from the stored criterion scores. Defaults: {CRITERIA.map((c) => `${c.short} ${c.weight}`).join(' · ')}.</p>
              {custom && (
                <button onClick={() => setWeights({ ...DEFAULT_WEIGHTS })} className="text-[11px] font-semibold text-[#717182] hover:text-[#18365E] flex items-center gap-1"><RotateCcw className="w-3 h-3" /> Reset to defaults</button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Epic baseline */}
      <SectionCard icon={<CheckCircle2 className="w-3.5 h-3.5" />} title="Start with what you own: Epic">
        <div className="grid md:grid-cols-2 gap-4 text-xs">
          <div>
            <p className="text-[10px] font-bold text-emerald-700 uppercase tracking-wide mb-1.5">Covered natively</p>
            <ul className="space-y-1">{evaluation.epicBaseline.covers.map((c) => <li key={c} className="flex gap-1.5 text-[#4b5563]"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" />{c}</li>)}</ul>
          </div>
          <div>
            <p className="text-[10px] font-bold text-[#FF6E4C] uppercase tracking-wide mb-1.5">Gaps that justify buying</p>
            <ul className="space-y-1">{evaluation.epicBaseline.gaps.map((g) => <li key={g} className="flex gap-1.5 text-[#4b5563]"><AlertTriangle className="w-3.5 h-3.5 text-[#FF6E4C] mt-0.5 shrink-0" />{g}</li>)}</ul>
          </div>
        </div>
        {evaluation.epicBaseline.sources.length > 0 && (
          <p className="text-[10px] text-[#717182] mt-3 flex flex-wrap gap-x-3">
            {evaluation.epicBaseline.sources.map((s) => <a key={s.url} href={s.url} target="_blank" rel="noreferrer" className="underline decoration-dotted hover:text-[#FF6E4C] inline-flex items-center gap-0.5">{s.title}<ExternalLink className="w-2.5 h-2.5" /></a>)}
          </p>
        )}
      </SectionCard>

      {/* Lots */}
      {lots.map(({ lot, ranked }, li) => {
        const shortlist = ranked.filter((v) => v.liveStatus === 'shortlist');
        const near = ranked.filter((v) => v.liveStatus === 'nearMiss');
        return (
          <section key={lot.id} className="rounded-xl border border-[#e5e7eb] bg-white p-4">
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="text-[10px] font-bold text-white bg-[#18365E] rounded px-1.5 py-0.5">Lot {String.fromCharCode(65 + li)}</span>
              <h3 className="text-sm font-bold text-[#18365E]">{lot.name}</h3>
              <span className="text-[11px] text-[#717182]">{ranked.length} evaluated · {shortlist.length} shortlisted</span>
            </div>
            <p className="text-xs text-[#4b5563] mt-1 max-w-4xl"><b className="text-[#18365E]">Gap:</b> {lot.gap} {lot.why}</p>

            {/* Shortlist */}
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3 mt-3">
              {shortlist.map((v) => (
                <article key={v.vendor} className="rounded-lg border-2 border-[#18365E]/15 bg-[#FAFBFC] p-3.5 text-xs flex flex-col">
                  <div className="flex items-start gap-2">
                    <span className="w-6 h-6 rounded-full bg-[#18365E] text-white text-[11px] font-bold flex items-center justify-center shrink-0">{v.liveRank}</span>
                    <h4 className="font-bold text-[#18365E] text-[13px] leading-snug flex-1">{v.vendor}</h4>
                    <span className="text-sm font-bold text-[#18365E] tabular-nums">{v.liveScore.toFixed(0)}</span>
                  </div>
                  <div className="flex gap-1 mt-2" aria-label="Criterion scores">
                    {CRITERIA.map((c) => {
                      const s = v.scores[c.id];
                      return (
                        <span key={c.id} title={`${c.name}: ${s?.score ?? '?'} of 5${s?.verify ? ' (verify)' : ''}. ${s?.rationale ?? ''}`}
                          className={`flex-1 text-center text-[10px] font-semibold rounded py-0.5 ${scoreTone(s?.score ?? 0)} ${s?.verify ? 'ring-1 ring-amber-400' : ''}`}>
                          {s?.score ?? '?'}
                        </span>
                      );
                    })}
                  </div>
                  <div className="flex gap-1 mt-0.5">{CRITERIA.map((c) => <span key={c.id} className="flex-1 text-center text-[8.5px] text-[#717182] truncate">{c.short}</span>)}</div>
                  {v.whyShortlisted ? (
                    <p className="text-[#4b5563] mt-2 leading-relaxed">{v.whyShortlisted}</p>
                  ) : (
                    <p className="text-[#717182] mt-2 italic">Moved onto the shortlist under your weights. {v.whyNot ?? ''}</p>
                  )}
                  {v.keyRisk && <p className="text-[11px] text-amber-800 mt-1.5 flex gap-1"><AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />{v.keyRisk}</p>}
                  {(v.verifyInDemo?.length || v.rfpQuestions?.length) ? (
                    <details className="mt-2 text-[11px]">
                      <summary className="cursor-pointer font-semibold text-[#18365E] hover:text-[#FF6E4C]">What to see in the demo · RFP questions</summary>
                      {v.verifyInDemo && v.verifyInDemo.length > 0 && (
                        <ul className="mt-1.5 space-y-0.5">{v.verifyInDemo.map((x) => <li key={x} className="flex gap-1 text-[#4b5563]"><ListChecks className="w-3 h-3 mt-0.5 shrink-0 text-[#18365E]" />{x}</li>)}</ul>
                      )}
                      {v.rfpQuestions && v.rfpQuestions.length > 0 && (
                        <ol className="mt-1.5 ml-4 list-decimal space-y-0.5 text-[#4b5563]">{v.rfpQuestions.map((q) => <li key={q}>{q}</li>)}</ol>
                      )}
                    </details>
                  ) : null}
                </article>
              ))}
            </div>

            {near.length > 0 && (
              <div className="mt-3 rounded-lg bg-[#F5F7FA] px-3 py-2.5">
                <p className="text-[10px] font-bold text-[#717182] uppercase tracking-wide mb-1">Near misses</p>
                <ul className="space-y-1">
                  {near.map((v) => (
                    <li key={v.vendor} className="text-xs text-[#4b5563]">
                      <b className="text-[#18365E]">{v.vendor}</b> <span className="text-[#717182] tabular-nums">({v.liveScore.toFixed(0)})</span>{v.whyNot ? `: ${v.whyNot}` : v.whyShortlisted ? `: shortlisted under default weights. ${v.whyShortlisted}` : ''}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Matrix */}
            <details className="mt-3">
              <summary className="text-xs font-semibold text-[#18365E] cursor-pointer hover:text-[#FF6E4C]">Evaluation matrix ({ranked.length} vendors, scores with rationale and evidence)</summary>
              <div className="overflow-x-auto mt-2 rounded-lg border border-[#e5e7eb]">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-[#F5F7FA] text-[10px] font-semibold text-[#717182] uppercase tracking-wide">
                      <th className="text-left px-2 py-2">#</th>
                      <th className="text-left px-2 py-2">Vendor</th>
                      <th className="text-right px-2 py-2">Score</th>
                      {CRITERIA.map((c) => <th key={c.id} className="px-1.5 py-2 text-center" title={c.name}>{c.short}<br /><span className="font-normal normal-case">w{weights[c.id]}</span></th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {ranked.map((v) => (
                      <tr key={v.vendor} className={`border-t border-[#f1f5f9] ${v.liveStatus === 'shortlist' ? 'bg-[#FFF7F4]' : ''}`}>
                        <td className="px-2 py-1.5 text-[#717182] tabular-nums">{v.liveRank}</td>
                        <td className="px-2 py-1.5 font-semibold text-[#18365E]">
                          {v.vendor}
                          {v.liveStatus === 'shortlist' && <span className="ml-1.5 text-[9px] font-bold text-[#FF6E4C] uppercase">shortlist</span>}
                        </td>
                        <td className="px-2 py-1.5 text-right font-bold text-[#18365E] tabular-nums">{v.liveScore.toFixed(1)}</td>
                        {CRITERIA.map((c) => {
                          const s = v.scores[c.id as CriterionId];
                          return (
                            <td key={c.id} className="px-1 py-1.5 text-center">
                              <span title={`${s?.rationale ?? ''}${s?.evidence?.length ? `\nEvidence: ${s.evidence.map(host).join(', ')}` : ''}${s?.verify ? '\nNeeds verification' : ''}`}
                                className={`inline-block min-w-[22px] rounded px-1 py-0.5 font-semibold cursor-help ${scoreTone(s?.score ?? 0)} ${s?.verify ? 'ring-1 ring-amber-400' : ''}`}>
                                {s?.score ?? '?'}
                              </span>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-[10px] text-[#717182] mt-1.5">1 to 5 per criterion; hover a score for the rationale and evidence. An amber ring means no evidence was found either way, so it scored 2 and needs verification.</p>
            </details>
          </section>
        );
      })}

      {evaluation.screenedOut.length > 0 && (
        <details className="rounded-xl border border-[#e5e7eb] bg-white px-4 py-3">
          <summary className="text-xs font-semibold text-[#18365E] cursor-pointer flex items-center gap-1.5"><XCircle className="w-3.5 h-3.5 text-[#717182]" />Screened out before scoring ({evaluation.screenedOut.length})</summary>
          <ul className="mt-2 space-y-1.5">
            {evaluation.screenedOut.map((x) => <li key={x.vendor} className="text-xs text-[#4b5563]"><b className="text-[#18365E]">{x.vendor}:</b> {x.reason}</li>)}
          </ul>
        </details>
      )}

      {(evaluation.openQuestions.length > 0 || evaluation.assumptions.length > 0) && (
        <details className="rounded-xl border border-[#e5e7eb] bg-white px-4 py-3">
          <summary className="text-xs font-semibold text-[#18365E] cursor-pointer">Assumptions and open questions</summary>
          {evaluation.assumptions.length > 0 && <ul className="mt-2 space-y-1 list-disc ml-4 text-xs text-[#4b5563]">{evaluation.assumptions.map((a) => <li key={a}>{a}</li>)}</ul>}
          {evaluation.openQuestions.length > 0 && <ul className="mt-2 space-y-1 list-disc ml-4 text-xs text-[#4b5563]">{evaluation.openQuestions.map((a) => <li key={a}>{a}</li>)}</ul>}
        </details>
      )}
    </div>
  );
};

export default ExecVendorEvaluation;
